// frontend/src/components/NavBar.jsx
// Updated: March 25, 2025
// Purpose: 

import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const NavBar = ({ user, onLogout, isAdmin = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const handleLogoClick = useCallback(() => {
    if (user) {
      navigate(isAdmin ? '/admin' : '/dashboard');
    } else {
      navigate('/');
    }
  }, [navigate, user, isAdmin]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-primary-600 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Logo */}
            <div 
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={handleLogoClick}
              role="button"
              tabIndex={0}
            >
              <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V16M8 12H16M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="ml-2 font-bold text-xl">Annuity Lead System</span>
            </div>
            
            {/* Desktop Links */}
            {user && (
              <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
                <Link
                  to={isAdmin ? '/admin' : '/dashboard'}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    isActive(isAdmin ? '/admin' : '/dashboard')
                      ? 'bg-primary-700 text-white'
                      : 'text-white hover:bg-primary-500'
                  }`}
                >
                  Dashboard
                </Link>
                
                {isAdmin ? (
                  <>
                    <Link
                      to="/admin/leads"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        isActive('/admin/leads')
                          ? 'bg-primary-700 text-white'
                          : 'text-white hover:bg-primary-500'
                      }`}
                    >
                      Leads
                    </Link>
                    <Link
                      to="/admin/emails"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        isActive('/admin/emails')
                          ? 'bg-primary-700 text-white'
                          : 'text-white hover:bg-primary-500'
                      }`}
                    >
                      Emails
                    </Link>
                    <Link
                      to="/admin/unbounce"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        location.pathname.includes('/admin/unbounce')
                          ? 'bg-primary-700 text-white'
                          : 'text-white hover:bg-primary-500'
                      }`}
                    >
                      Unbounce
                    </Link>
                    <Link
                      to="/admin/agents"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        isActive('/admin/agents')
                          ? 'bg-primary-700 text-white'
                          : 'text-white hover:bg-primary-500'
                      }`}
                    >
                      Agents
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/leads"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        isActive('/leads')
                          ? 'bg-primary-700 text-white'
                          : 'text-white hover:bg-primary-500'
                      }`}
                    >
                      Leads
                    </Link>
                    <Link
                      to="/profile"
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        isActive('/profile')
                          ? 'bg-primary-700 text-white'
                          : 'text-white hover:bg-primary-500'
                      }`}
                    >
                      Profile
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Right Side */}
          {user && (
            <div className="flex items-center">
              <div className="hidden sm:block">
                <div className="flex items-center">
                  <span className="text-sm mr-2">{user.name || user.email}</span>
                  <button
                    onClick={onLogout}
                    className="px-3 py-2 text-sm bg-primary-700 hover:bg-primary-800 text-white rounded-md"
                  >
                    Logout
                  </button>
                </div>
              </div>
              
              {/* Mobile menu button */}
              <div className="sm:hidden -mr-2 flex items-center">
                <button
                  onClick={toggleMenu}
                  className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <span className="sr-only">Open main menu</span>
                  {menuOpen ? (
                    <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="sm:hidden bg-primary-700 pb-3 pt-2">
          <div className="px-2 space-y-1">
            <Link
              to={isAdmin ? '/admin' : '/dashboard'}
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive(isAdmin ? '/admin' : '/dashboard')
                  ? 'bg-primary-800 text-white'
                  : 'text-white hover:bg-primary-600'
              }`}
              onClick={toggleMenu}
            >
              Dashboard
            </Link>
            
            {isAdmin ? (
              <>
                <Link
                  to="/admin/leads"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/admin/leads')
                      ? 'bg-primary-800 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                  onClick={toggleMenu}
                >
                  Leads
                </Link>
                <Link
                  to="/admin/emails"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/admin/emails')
                      ? 'bg-primary-800 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                  onClick={toggleMenu}
                >
                  Emails
                </Link>
                <Link
                  to="/admin/unbounce"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    location.pathname.includes('/admin/unbounce')
                      ? 'bg-primary-800 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                  onClick={toggleMenu}
                >
                  Unbounce
                </Link>
                <Link
                  to="/admin/agents"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/admin/agents')
                      ? 'bg-primary-800 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                  onClick={toggleMenu}
                >
                  Agents
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/leads"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/leads')
                      ? 'bg-primary-800 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                  onClick={toggleMenu}
                >
                  Leads
                </Link>
                <Link
                  to="/profile"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/profile')
                      ? 'bg-primary-800 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                  onClick={toggleMenu}
                >
                  Profile
                </Link>
              </>
            )}
            
            <button
              onClick={() => { onLogout(); toggleMenu(); }}
              className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-primary-800 hover:bg-primary-900"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;